import { type User } from "@internal/user";
import {
  enumeratedLiterals,
  type EnumeratedLiteralModel,
  type EnumeratedLiteralType,
} from "@north-beam/nb-common";

import { AccountManagerStep as AccountManagerStepV2 } from "./core/account-manager-step";
import { ConfigurePlatformsStep } from "./core/configure-platforms-step";
import { Currency } from "./core/currency";
import { DnsEntryStep } from "./core/dns-entry-step";
import { DomainCollection } from "./core/domain-collection";
import { Finish } from "./core/finish";
import { IntroductoryVideo } from "./core/introductory-video";
import { OrdersApi } from "./core/orders-api";
import { PixelPlacementSteps } from "./core/pixel-placement";
import { SpendApiStep } from "./core/spend-api-step";
import { UserProfileCollectionStep } from "./core/user-profile-collection-step";
import { Verify } from "./core/verify";

export type NavConfig = {
  readonly label: string;
  readonly icon: string;
} | null;

export const Steps = enumeratedLiterals(
  [
    "introduction",
    "account-managers",
    "basic-details",
    "currency",
    "domain",
    "dns-setup",
    "pixel-placement",
    "orders-api",
    "configure-platforms",
    "spend-api",
    "verify",
    "complete",
  ] as const,
  {
    getAttributes: (a) =>
      ({
        [a.INTRODUCTION]: {
          component: IntroductoryVideo,
          isVisible: undefined,
          navConfig: {
            label: "Welcome to Northbeam!",
            icon: "solar-system",
          } satisfies NavConfig,
        },
        [a.ACCOUNT_MANAGERS]: {
          component: AccountManagerStepV2,
          isVisible: undefined,
          navConfig: {
            label: "Invite Team",
            icon: "users",
          } satisfies NavConfig,
        },
        [a.BASIC_DETAILS]: {
          component: UserProfileCollectionStep,
          isVisible: undefined,
          navConfig: {
            label: "Business Information",
            icon: "building",
          } satisfies NavConfig,
        },
        [a.CURRENCY]: {
          component: Currency,
          isVisible: (user: User) =>
            user.featureFlags.enableConfigurableCurrencies,
          navConfig: {
            label: "Currency",
            icon: "dollar-sign",
          } satisfies NavConfig,
        },
        [a.DOMAIN]: {
          component: DomainCollection,
          isVisible: (user: User) => !user.isPixelConfigurationSubaccount,
          navConfig: {
            label: "Add Domain",
            icon: "globe",
          } satisfies NavConfig,
        },

        [a.DNS_SETUP]: {
          component: DnsEntryStep,
          isVisible: (user: User) => !user.isPixelConfigurationSubaccount,
          navConfig: {
            label: "Link DNS",
            icon: "radar",
          } satisfies NavConfig,
        },
        [a.PIXEL_PLACEMENT]: {
          component: PixelPlacementSteps,
          isVisible: (user: User) => !user.isPixelConfigurationSubaccount,
          navConfig: {
            label: "Add Pixel",
            icon: "cube",
          } satisfies NavConfig,
        },
        [a.ORDERS_API]: {
          component: OrdersApi,
          isVisible: undefined,
          navConfig: {
            label: "Add Orders",
            icon: "bag-shopping",
          } satisfies NavConfig,
        },
        [a.CONFIGURE_PLATFORMS]: {
          component: ConfigurePlatformsStep,
          isVisible: undefined,
          navConfig: {
            label: "Integrations and UTMs",
            icon: "circle-nodes",
          } satisfies NavConfig,
        },
        [a.SPEND_API]: {
          component: SpendApiStep,
          isVisible: undefined,
          navConfig: {
            label: "Spend API",
            icon: "money-bill",
          } satisfies NavConfig,
        },
        [a.VERIFY]: {
          component: Verify,
          isVisible: undefined,
          navConfig: {
            label: "Verify Your Setup",
            icon: "check-circle",
          } satisfies NavConfig,
        },
        [a.COMPLETE]: {
          component: Finish,
          isVisible: undefined,
          navConfig: {
            label: "Finish",
            icon: "check-circle",
          } satisfies NavConfig,
        },
      } as const),
  },
);

export type Step = EnumeratedLiteralModel<typeof Steps>;
export type StepName = EnumeratedLiteralType<typeof Steps>;

export const stepIsVisible = (step: Step | StepName, user: User): boolean => {
  let stepDetails: Step;
  if (typeof step === "string") {
    stepDetails = Steps.getModel(step);
  } else {
    stepDetails = step;
  }
  return stepDetails.isVisible !== undefined
    ? stepDetails.isVisible(user)
    : true;
};

export const stepIsLast = (step: Step | StepName, user: User): boolean => {
  let st: Step;
  if (typeof step === "string") {
    st = Steps.getModel(step);
  } else {
    st = step;
  }
  if (!stepIsVisible(st, user)) {
    return false;
  }

  const name = Steps.contains(step) ? step : step.value;
  const index = Steps.data.findIndex((s) => s.value === name);

  if (
    Steps.data
      .slice(index + 1)
      .filter((s) => stepIsVisible(s, user) && s.value !== Steps.COMPLETE)
      .length === 0
  ) {
    return true;
  }
  return false;
};
